import React from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Typography } from '@mui/material'
import { PromotionCodeOverviewResponse } from '../../models'
import { Formik } from 'formik'
import { TextField } from 'src/Libs/inputs/formik'
import * as Yup from 'yup'
import { useShopAdmin } from '../../hooks'
import { makeStyles } from 'tss-react/mui'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { currencyFormatter } from '../../utils'

interface ShopPromotionCodeModalProps {
  place: number
  totalAmount: number
  existingPromoCode?: { id: string; amount: number; editable: boolean; note?: string | null }
  onClose(): void
  onConfirm: (data: PromotionCodeOverviewResponse) => void
}

interface FormInputType {
  amount: number
  note: string
}

const useStyles = makeStyles()(theme => ({
  container: {
    paddingTop: theme.spacing(2),
  },
  info: {
    paddingBottom: theme.spacing(2),
  },
}))

const MIN_INPUT_NUMBER = 1
/*
 * This component shows the modal of promotion code modal
 * It allows the user to add or edit a existing promo code price amount
 */
const ShopPromotionCodeModal: React.FC<ShopPromotionCodeModalProps> = ({ place, totalAmount, existingPromoCode, onClose, onConfirm }) => {
  const { t } = useCustomTranslation()
  const { createPromotionCode, updatePromotionCode } = useShopAdmin()
  const { enqueueSuccessStack } = useSnackbar()
  const { classes } = useStyles()
  const availableAmount = existingPromoCode?.amount ? Number(existingPromoCode.amount) + Number(totalAmount) : totalAmount
  const dialogTitle = existingPromoCode ? t('shop.promotion-code.edit-promotion-code-label') : t('shop.promotion-code.add-promotion-code-label')
  const promotionCodeLabel = existingPromoCode ? t('shop.promotion-code.edit-available-amount-text') : t('shop.promotion-code.available-amount-text')
  const editable = existingPromoCode?.editable ?? true

  const handleSubmit = async (formData: FormInputType): Promise<void> => {
    if (existingPromoCode) {
      return updatePromotionCode(existingPromoCode.id, Number(formData.amount), formData.note).then(result => {
        if (result !== null) {
          onConfirm(result)
          enqueueSuccessStack(t('shop.promotion-code.promo-code-added'))
          onClose()
        }
      })
    }

    return createPromotionCode(Number(formData.amount), formData.note).then(result => {
      if (result !== null) {
        onConfirm(result)
        enqueueSuccessStack(t('shop.promotion-code.promo-code-added'))
        onClose()
      }
    })
  }

  const initialValues: FormInputType = {
    amount: existingPromoCode ? existingPromoCode.amount : 0,
    note: existingPromoCode?.note ? existingPromoCode.note : '',
  }

  const validationSchema = Yup.object().shape({
    amount: Yup.number()
      .test('max', t('validation.promotion-code.amount-is-bigger-than-limit'), function (value) {
        return value !== undefined && value <= availableAmount
      })
      .min(MIN_INPUT_NUMBER, 'validation.promotion-code.amount-must-be-minimum-number')
      .positive(t('validation.promotion-code.amount-must-be-positive'))
      .required(t('validation.promotion-code.amount-is-required')),
  })

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ dirty, isValid, handleSubmit }) => {
        const dialogActions = {
          primary: {
            disabled: !isValid && !dirty,
            name: t('shop.common.confirm'),
            onClick: handleSubmit,
          },
          secondary: [
            {
              name: t('shop.common.cancel'),
              onClick: () => {
                onClose()
              },
            },
          ],
        }
        return (
          <Dialog title={dialogTitle} maxWidth="xs" place={place} actions={dialogActions} disableClose blurBackground>
            <Box className={classes.container}>
              {editable && (
                <Typography className={classes.info} variant="h4">
                  {`${promotionCodeLabel}: ${currencyFormatter(availableAmount, 0)}`}
                </Typography>
              )}
              <form onSubmit={handleSubmit}>
                {editable && (
                  <TextField
                    className={classes.info}
                    name="amount"
                    type="number"
                    fullWidth
                    label={t('shop.promotion-code.promotion-code-placeholder')}
                    required
                  />
                )}
                <TextField multiline name="note" fullWidth label={t('shop.promotion-code.promotion-code-note-placeholder')} />
              </form>
            </Box>
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default ShopPromotionCodeModal
